import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, IconButton, Chip, TextField, FormControl, InputLabel, Select, MenuItem, Link
} from '@mui/material';
import { format } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import { Project, ProjectStatus } from '../types/project';
import { projectService } from '../services/projectService';
import AddProjectModal from '../components/AddProjectModal';
import ModifyProjectModal from '../components/ModifyProjectModal';
import { useDebounce } from '../hooks/useDebounce';

const statusColors: Record<ProjectStatus, 'default' | 'info' | 'success' | 'error'> = {
  PENDING: 'default',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const fetchProjects = async (search: string, status: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await projectService.getProjects({ search, status });
      setProjects(data);
    } catch (err) {
      setError('Failed to fetch projects.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(debouncedSearchTerm, statusFilter);
  }, [debouncedSearchTerm, statusFilter]);

  const handleModifyClick = (project: Project) => {
    setSelectedProject(project);
    setIsModifyModalOpen(true);
  };

  const handleSave = () => {
    fetchProjects(debouncedSearchTerm, statusFilter);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Projects</Typography>
        <Button variant="contained" onClick={() => setIsAddModalOpen(true)}>Add Project</Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box sx={{ flex: { sm: 2 } }}> {/* Takes up 2/3 of the space on sm screens and up */}
          <TextField
            fullWidth
            label="Search Projects (by Name, Description, Client)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>
        <Box sx={{ flex: { sm: 1 } }}> {/* Takes up 1/3 of the space on sm screens and up */}
          <FormControl fullWidth>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as string)}
            >
              <MenuItem value=""><em>All Statuses</em></MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project Name</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Project Value</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell>
                  <Link component={RouterLink} to={`/projects/${project.id}`} underline="hover">
                    {project.name}
                  </Link>
                </TableCell>
                <TableCell>{project.client_details?.name || `Client ID: ${project.client}`}</TableCell>
                <TableCell>
                  {project.project_value ? `$${Number(project.project_value).toFixed(2)}` : 'N/A'}
                </TableCell>
                <TableCell>{project.start_date ? format(new Date(project.start_date), 'MMM dd, yyyy') : 'N/A'}</TableCell>
                <TableCell>{project.end_date ? format(new Date(project.end_date), 'MMM dd, yyyy') : 'N/A'}</TableCell>
                <TableCell>
                  <Chip label={project.status.replace('_', ' ')} color={statusColors[project.status]} size="small" sx={{ textTransform: 'capitalize' }} />
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleModifyClick(project)}><EditIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AddProjectModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleSave} />
      <ModifyProjectModal 
        open={isModifyModalOpen} 
        onClose={() => setIsModifyModalOpen(false)} 
        onSave={handleSave}
        project={selectedProject}
      />
    </Box>
  );
};

export default ProjectsPage;